// License discrepancies filter registry. Drives the Discrepancies tab on the
// license detail page. The data isn't backed by a single SQL table — the
// `LicenseDiscrepancyService` computes the rows in-process from the live
// registry comparison plus the snooze table — so `queryMapping` is set to
// `'custom'` for both filters and the backend reads them straight off the
// `filters` object.

import type { FilterRegistry } from './types';

const LICENSE_DISCREPANCIES_LIST_KEYS: ReadonlyArray<string> = [
	'license-discrepancies-embedded',
];

export const licenseDiscrepanciesFilters: FilterRegistry = {
	discFieldName: {
		type: 'multi-select',
		label: 'Field',
		placeholder: 'Select field...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		// Labels deliberately reuse the same wording as the columns on the
		// table so the filter chip matches what the user just clicked.
		// PD-14512: no 'status' option — the status signal renders as an alert
		// banner above the table (it is not a stored-vs-registry diff row), so
		// a Status filter would only ever produce an empty list.
		options: [
			{ id: 'expirationDate', label: 'Expiration Date' },
			{ id: 'licenseNumber', label: 'License Number' },
			{ id: 'name', label: 'Name' },
		],
		queryParamName: 'discFieldName',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			// In-memory filter — the service reads `filters.discFieldName`
			// directly and narrows the row set.
			objectTable: 'license_discrepancy',
			fieldName: 'field',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...LICENSE_DISCREPANCIES_LIST_KEYS],
	},

	discSnoozeState: {
		type: 'single-select',
		label: 'Snooze State',
		placeholder: 'Show...',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		allowClear: false,
		// Three-way facet. Default behaviour is 'active' (matches the alerts
		// card count); 'snoozed' shows only suppressed rows; 'all' shows
		// both for audit-trail review.
		options: [
			{ id: 'active', label: 'Active alerts' },
			{ id: 'snoozed', label: 'Snoozed' },
			{ id: 'all', label: 'All' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'discSnoozeState',
		multiple: false,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'license_discrepancy',
			fieldName: 'snoozeState',
			joinType: 'custom',
		},
		format: 'single',
		listKeys: [...LICENSE_DISCREPANCIES_LIST_KEYS],
	},
};
